import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";

// Residential sub-items — built so we can add more pages later without touching code
const RESIDENTIAL_SUB = [
  { to: "/residential", label: "Residential Overview", desc: "All residential services" },
  { to: "/residential/outdoor-living", label: "Outdoor Living", desc: "Flagship — patios, fire, kitchens, light" },
  { to: "/residential/landscape-design", label: "Landscape Design", desc: "Architect-led design for homes" },
  { to: "/residential/paving-stones", label: "Paving Stones", desc: "Patios, walkways, driveways" },
  { to: "/residential/retaining-walls", label: "Retaining Walls", desc: "Engineered walls & terracing" },
  { to: "/residential/fence-builders", label: "Fence Builders", desc: "Wood, vinyl, metal fences" },
  { to: "/residential/tree-planting", label: "Tree Planting", desc: "Mature trees & shrubs" },
  { to: "/residential/sod-installation", label: "Sod Installation", desc: "Fresh lawn, same-day install" },
  { to: "/residential/decks", label: "Decks", desc: "Composite & hardwood decks" },
  { to: "/residential/fire-features", label: "Fire Features", desc: "Fire pits & outdoor fireplaces" },
  { to: "/residential/pergolas", label: "Pergolas", desc: "Shade structures & louvers" },
  { to: "/residential/hardscaping", label: "Hardscaping", desc: "Walkways, steps, stone walls" },
];

const HOME_SUB = [
  { to: "/", label: "Home", desc: "Back to the homepage" },
  { to: "/about", label: "About", desc: "Our studio, team & values" },
  { to: "/blog", label: "Journal", desc: "Guides, pricing & seasonal tips" },
];

const COMMERCIAL_SUB = [
  { to: "/commercial", label: "Commercial Overview", desc: "Landscaping, maintenance & winter care" },
  { to: "/commercial/snow-removal", label: "Snow Removal", desc: "Lots, sidewalks, sanding & hauling" },
];

const LINKS = [
  { to: "/", label: "Home", sub: HOME_SUB },
  { to: "/residential", label: "Residential", sub: RESIDENTIAL_SUB },
  { to: "/commercial", label: "Commercial", sub: COMMERCIAL_SUB },
  { to: "/projects", label: "Projects" },
  { to: "/yard-designer", label: "AI Designer" },
  { to: "/contact", label: "Contact" },
];

function DesktopLink({ link }) {
  const [open, setOpen] = useState(false);

  if (!link.sub) {
    return (
      <NavLink
        to={link.to}
        data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
        className={({ isActive }) =>
          `text-sm tracking-wide link-underline transition-colors duration-300 ${
            isActive ? "text-[#F5F3EF]" : "text-[#D8D6D0]/70 hover:text-[#F5F3EF]"
          }`
        }
        end={link.to === "/"}
      >
        {link.label}
      </NavLink>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <NavLink
        to={link.to}
        data-testid={`nav-link-${link.label.toLowerCase()}`}
        end={link.to === "/"}
        className={({ isActive }) =>
          `inline-flex items-center gap-1 text-sm tracking-wide link-underline transition-colors duration-300 ${
            isActive ? "text-[#F5F3EF]" : "text-[#D8D6D0]/70 hover:text-[#F5F3EF]"
          }`
        }
      >
        {link.label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </NavLink>

      <div
        className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 transition-opacity duration-200 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="bg-[#151515] border border-[#D8D6D0]/15 shadow-2xl min-w-[280px] p-2">
          {link.sub.map((s) => (
            <Link
              key={s.to}
              to={s.to}
              data-testid={`nav-sub-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="block px-4 py-3 hover:bg-[#0B2B1F] transition-colors group"
            >
              <div className="font-serif-display text-base text-[#F5F3EF] group-hover:text-[#E2B85A] transition-colors">
                {s.label}
              </div>
              <div className="text-xs text-[#D8D6D0]/55 mt-0.5">{s.desc}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const loc = useLocation();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    h();
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => { setOpen(false); setMobileExpanded(null); }, [loc.pathname]);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-2xl bg-[#151515]/80 border-b border-[#D8D6D0]/10"
          : "bg-gradient-to-b from-black/70 via-black/30 to-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-16 h-28 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src="/logo.png"
            alt="NorthCrest"
            className="w-20 h-20 md:w-24 md:h-24 object-contain transition-transform duration-700 group-hover:scale-110"
          />
          <div className="flex flex-col leading-none">
            <span className="font-serif-display text-2xl md:text-3xl tracking-tight text-[#F5F3EF]">NorthCrest</span>
            <span className="overline text-[11px] text-[#D8D6D0]/50 tracking-[0.3em] mt-1">Landscaping</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => <DesktopLink key={l.to} link={l} />)}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/estimate"
            data-testid="nav-cta-estimate"
            className="inline-flex items-center px-5 py-3 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-sm tracking-wide transition-colors duration-300"
          >
            Get Free Estimate
          </Link>
        </div>

        <button
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-[#F5F3EF]"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#151515] border-t border-[#D8D6D0]/10 px-6 py-6 space-y-3 max-h-[80vh] overflow-y-auto shadow-2xl">
          {LINKS.map((l) => (
            <div key={l.to}>
              {l.sub ? (
                <>
                  <button
                    onClick={() => setMobileExpanded(mobileExpanded === l.label ? null : l.label)}
                    data-testid={`nav-mobile-toggle-${l.label.toLowerCase()}`}
                    className="w-full flex items-center justify-between text-lg font-serif-display py-2"
                  >
                    <span>{l.label}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === l.label ? "rotate-180" : ""}`} />
                  </button>
                  {mobileExpanded === l.label && (
                    <div className="pl-4 mt-2 space-y-3 border-l border-[#D8D6D0]/15">
                      {l.sub.map((s) => (
                        <Link
                          key={s.to}
                          to={s.to}
                          data-testid={`nav-mobile-sub-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className="block text-sm text-[#D8D6D0]/80 hover:text-[#F5F3EF] py-1"
                        >
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="block text-lg font-serif-display py-2"
                >
                  {l.label}
                </Link>
              )}
            </div>
          ))}
          <Link
            to="/estimate"
            data-testid="nav-mobile-cta"
            className="block w-full text-center px-5 py-4 bg-[#8C1D30] text-[#F5F3EF] mt-4"
          >
            Get Free Estimate
          </Link>
        </div>
      )}
    </header>
  );
}
